import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRightLeft, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-admin/bridge`;
const LABELS = { base: 'Base', arbitrum: 'Arbitrum' };
const f = (v, d = 6) => Number(v || 0).toFixed(d);

export const BridgeEthCard = ({ token }) => {
  const [to, setTo] = useState('base');
  const [amount, setAmount] = useState('0.002');
  const [quote, setQuote] = useState(null);
  const [hist, setHist] = useState(null);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [sending, setSending] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadHist = () => axios.get(`${API}/transfers`, { headers }).then(r => setHist(r.data)).catch(() => {});
  useEffect(() => { loadHist(); }, [token]);

  const getQuote = async () => {
    setQuote(null);
    try { setQuote((await axios.get(`${API}/quote`, { params: { to, amount_eth: amount }, headers })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not estimate'); }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(API, { to, amount_eth: parseFloat(amount), confirm_text: typed.trim() }, { headers });
      toast.success(`Sent ${r.data.amount_eth} ETH to ${LABELS[to]}; arrives in about ${r.data.arrival_minutes} minutes`);
      setOpen(false); setQuote(null); loadHist();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bridge failed'); } finally { setSending(false); }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4" data-testid="bridge-eth-card">
      <div className="flex items-center gap-3"><ArrowRightLeft className="w-5 h-5 text-sky-400" /><h3 className="text-white font-semibold">Move ETH to Base / Arbitrum</h3></div>
      <p className="text-sm text-slate-400">Moves the deployment wallet's ETH from Ethereum to the same address on Base or Arbitrum using their official bridges.</p>
      {hist && <p className="text-xs text-slate-300" data-testid="bridge-balances">On Base: {f(hist.balances.base)} ETH · On Arbitrum: {f(hist.balances.arbitrum)} ETH</p>}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-2">
          {Object.keys(LABELS).map(n => (
            <button key={n} onClick={() => { setTo(n); setQuote(null); }} data-testid={`bridge-to-${n}`}
              className={`px-3 py-2 rounded text-sm ${to === n ? 'bg-sky-500 text-white' : 'bg-slate-700 text-slate-300'}`}>{LABELS[n]}</button>
          ))}
        </div>
        <label className="text-sm text-slate-300 space-y-1">Amount (ETH)
          <input type="number" step="0.0005" min="0.0005" max="0.01" value={amount} onChange={(e) => { setAmount(e.target.value); setQuote(null); }}
            data-testid="bridge-amount-input" className="block w-32 bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white font-mono" />
        </label>
        <button onClick={getQuote} disabled={!(amount > 0)} data-testid="bridge-quote-btn" className="px-4 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600 text-white">Preview</button>
        <button onClick={() => { setTyped(''); setOpen(true); }} disabled={!quote?.sufficient} data-testid="bridge-submit-btn"
          className="px-4 py-2 text-sm rounded bg-sky-500 hover:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white">Move ETH</button>
      </div>
      {quote && (
        <div className="text-sm rounded bg-slate-900/60 p-3 space-y-1" data-testid="bridge-quote">
          <p className="text-white">{quote.amount_eth} ETH (${quote.amount_usd}) → {LABELS[quote.to]} · Ethereum fee ≈ {f(quote.fee_eth)} ETH (${quote.fee_usd})</p>
          <p className="text-xs text-slate-400">Ethereum balance {f(quote.ethereum_balance_eth)} ETH · arrives in about {quote.arrival_minutes} minutes</p>
          {!quote.sufficient && <p className="text-xs text-red-400" data-testid="bridge-insufficient">Not enough ETH on Ethereum. The most you can move now is {quote.max_eth} ETH.</p>}
        </div>
      )}
      {hist?.transfers?.length > 0 && (
        <div className="space-y-1" data-testid="bridge-history">
          {hist.transfers.map(t => (
            <a key={t.tx_hash} href={t.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-blue-400">
              <ExternalLink className="w-3 h-3" /> {new Date(t.at).toLocaleString()} · {t.amount_eth} ETH → {LABELS[t.to]}
            </a>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="bg-slate-900 border-amber-500/40 text-white" data-testid="bridge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300">Move {amount} ETH to {LABELS[to]}</DialogTitle>
            <DialogDescription className="text-slate-400">Real ETH from {quote?.wallet}. Ethereum fee ≈ ${quote?.fee_usd}. This can't be undone.</DialogDescription>
          </DialogHeader>
          <label className="text-sm text-slate-300">Type <code className="text-amber-300">{quote?.confirm_text}</code> to continue</label>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="bridge-confirm-input"
            className="w-full bg-slate-800 border border-slate-600 rounded-md px-3 py-2 text-white font-mono" />
          <DialogFooter>
            <button onClick={() => setOpen(false)} data-testid="bridge-confirm-cancel-btn" className="px-4 py-2 text-sm text-slate-300">Cancel</button>
            <button onClick={send} disabled={typed.trim() !== quote?.confirm_text || sending} data-testid="bridge-confirm-submit-btn"
              className="px-4 py-2 text-sm rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white flex items-center gap-2">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />} Move ETH
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BridgeEthCard;
